import { useState, useEffect } from 'react';
import { 
  Search, Filter, CheckCircle, XCircle, Clock, Users, 
  Star, TrendingUp, Calendar, AlertTriangle, RefreshCw,
  BarChart3, DollarSign, Home, Menu, X, Phone, Database, Settings as SettingsIcon
} from 'lucide-react';
import { useRoomManagement } from '../../hooks/useRoomManagementFirebase';
import { initializeHotelRooms } from '../../services/initializeRooms';
import { 
  getRoomReservations, 
  type Reservation 
} from '../../services/reservationService';
import { EnhancedCheckInModal } from './EnhancedCheckInModal';

// Define Room interface locally for now
interface Room {
  room_number: string;
  type: 'Single' | 'Royal' | 'Suite';
  floor: number;
  status: 'available' | 'occupied' | 'cleaning' | 'maintenance' | 'out_of_order';
  price: number;
  amenities: string[];
  current_guest?: {
    name: string;
    phone: string;
    check_in: string;
    check_out: string;
  } | null;
  check_in?: string | null;
  check_out?: string | null;
  last_cleaned: string;
  maintenance_notes: string;
  booking_history: Array<any>;
}

export function RoomManagement() {
  const {
    rooms,
    statistics,
    analytics,
    loading,
    updateRoomStatus,
    checkInGuest,
    checkOutGuest,
    refreshData,
    isConnected
  } = useRoomManagement();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'management' | 'analytics' | 'data' | 'settings'>('overview');

  const handleInitializeRooms = async () => {
    if (!confirm('Initialize 27 rooms for Hotel A.S.K Grand?\n\n5 Suite + 14 Royal + 8 Single rooms')) {
      return;
    }
    
    try {
      const success = await initializeHotelRooms();
      if (success) {
        alert('✅ Successfully initialized 27 rooms! Refreshing...');
        window.location.reload();
      } else {
        alert('❌ Failed to initialize rooms. Check console.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error: ' + (error as Error).message);
    }
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || room.status === filterStatus;
    const matchesType = filterType === 'all' || room.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: Room['status']) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'occupied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'maintenance':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cleaning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'out_of_order':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: Room['status']) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="w-4 h-4" />;
      case 'occupied':
        return <Users className="w-4 h-4" />;
      case 'maintenance':
        return <AlertTriangle className="w-4 h-4" />;
      case 'cleaning':
        return <Clock className="w-4 h-4" />;
      case 'out_of_order':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-luxury-gradient">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading luxury management system...</p>
          <p className="text-cream-300 text-sm mt-2">Hotel A.S.K Grand</p>
        </div>
      </div>
    );
  }

  // Show initialization prompt if no rooms
  if (!loading && rooms.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-luxury-gradient p-4">
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-10 max-w-2xl border border-gold-400/20 text-center">
          <div className="w-20 h-20 bg-gold-gradient rounded-full flex items-center justify-center mx-auto mb-6">
            <Database className="w-10 h-10 text-navy-900" />
          </div>
          <h2 className="text-3xl font-bold text-gold-400 mb-4">Welcome to Hotel A.S.K Grand</h2>
          <p className="text-cream-200 text-lg mb-6">
            Your Firebase database is connected and ready!
          </p>
          <div className="bg-white/10 rounded-xl p-6 mb-6 text-left">
            <h3 className="text-gold-400 font-bold mb-3">Initialize Your Hotel:</h3>
            <ul className="text-cream-200 space-y-2">
              <li>• Floor 1: Suite 101-105 (5 rooms) @ ₹1,750/night</li>
              <li>• Floor 2: Royal 201-207 (7 rooms) @ ₹1,500/night</li>
              <li>• Floor 3: Royal 301-307 (7 rooms) @ ₹1,500/night</li>
              <li>• Floor 4: Single 401-408 (8 rooms) @ ₹900/night</li>
              <li className="text-gold-400 font-bold pt-2">Total: 27 rooms</li>
            </ul>
          </div>
          <button
            onClick={handleInitializeRooms}
            className="bg-gold-gradient text-navy-900 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-gold-xl transition-all transform hover:scale-105 flex items-center gap-3 mx-auto"
          >
            <Database className="w-6 h-6" />
            Initialize 27 Rooms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-gradient">
      {/* Luxury Header */}
      <div className="glass-luxury border-b border-gold-400/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <img src="/Logo.png" alt="Hotel Logo" className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-bold text-gold-400">Hotel A.S.K Grand</h1>
                <p className="text-sm text-cream-300">Luxury Management System</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className="hidden sm:inline">{isConnected ? 'Connected' : 'Offline'}</span>
              </div>
              
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 text-gold-400 hover:bg-gold-400/10 rounded-lg"
              >
                {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className={`${showMobileMenu ? 'block' : 'hidden'} lg:block pb-4`}>
            <div className="flex flex-col lg:flex-row gap-2">
              {[
                { id: 'overview', label: 'Dashboard Overview', icon: BarChart3 },
                { id: 'management', label: 'Room Management', icon: Home },
                { id: 'analytics', label: 'Analytics Dashboard', icon: TrendingUp },
                { id: 'data', label: 'Data Management', icon: Database },
                { id: 'settings', label: 'Settings', icon: SettingsIcon }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    data-tab={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                      activeTab === tab.id 
                        ? 'bg-gold-gradient text-navy-900 shadow-gold' 
                        : 'text-cream-200 hover:bg-gold-400/10 hover:text-gold-400'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'management' && (
          <SuiteManagementTab
            rooms={filteredRooms}
            statistics={statistics}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterType={filterType}
            setFilterType={setFilterType}
            updateRoomStatus={updateRoomStatus}
            checkInGuest={checkInGuest}
            checkOutGuest={checkOutGuest}
            refreshData={refreshData}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
          />
        )}

        {activeTab === 'overview' && (
          <OverviewDashboard 
            rooms={rooms}
            statistics={statistics}
            refreshData={refreshData}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab analytics={analytics} statistics={statistics} />
        )}

        {activeTab === 'data' && (
          <LuxuryDataManagement />
        )}

        {activeTab === 'settings' && (
          <LuxurySettings />
        )}
      </div>
    </div>
  );
}

// Room Card Component with Reservation Display
function RoomCard({ room, getStatusColor, getStatusIcon, updateRoomStatus, handleCheckIn, handleCheckOut }: any) {
  const [reservations, setReservations] = useState<Reservation[]>([]);

  useEffect(() => {
    loadReservations();
  }, [room.room_number]);

  const loadReservations = async () => {
    try {
      const roomReservations = await getRoomReservations(room.room_number);
      setReservations(roomReservations);
    } catch (error) {
      console.error('Error loading reservations:', error);
    }
  };

  // Get next upcoming reservation
  const nextReservation = reservations.find(res => 
    res.status === 'confirmed' && res.checkIn > new Date().toISOString().split('T')[0]
  );

  // Get current guest from reservation
  const currentReservation = reservations.find(res => {
    const today = new Date().toISOString().split('T')[0];
    return res.status === 'checked-in' && res.checkIn <= today && res.checkOut > today;
  });

  // Calculate available until date
  const getAvailableUntil = () => {
    if (nextReservation) {
      return new Date(nextReservation.checkIn).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      });
    }
    return 'Open';
  };

  return (
    <div className="glass-luxury rounded-3xl shadow-luxury-xl p-8 border border-gold-400/20 hover:shadow-gold-xl transition-all duration-300 min-h-[400px]">
      {/* Room Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-2xl font-bold text-gold-400 mb-2">Room {room.room_number}</h3>
          <p className="text-cream-300 text-lg">{room.type} Room</p>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 border ${getStatusColor(room.status)}`}>
          {getStatusIcon(room.status)}
          {room.status.charAt(0).toUpperCase() + room.status.slice(1)}
        </span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <span className="text-3xl font-bold text-gold-400">₹{room.price}</span>
        <div className="text-right">
          <p className="text-sm text-cream-400">per night</p>
        </div>
      </div>

      {/* Availability Status - only show if there's a next reservation */}
      {room.status === 'available' && nextReservation && (
        <div className="mb-4 p-3 bg-green-500/10 rounded-xl border border-green-400/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-green-300 font-medium">Available until:</span>
            <span className="text-green-400 font-bold">{getAvailableUntil()}</span>
          </div>
        </div>
      )}

      {/* Next Reservation Info */}
      {nextReservation && room.status === 'available' && (
        <div className="mb-4 p-3 bg-blue-500/10 rounded-xl border border-blue-400/20">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300 font-medium">Next Booking:</span>
          </div>
          <p className="text-blue-200 font-medium">{nextReservation.guestName}</p>
          <p className="text-xs text-blue-300">
            {new Date(nextReservation.checkIn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(nextReservation.checkOut).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </p>
        </div>
      )}

      {/* Current Guest Info */}
      {(room.current_guest || currentReservation) && (
        <div className="mb-6 p-4 bg-blue-500/20 rounded-xl border border-blue-400/30">
          <h4 className="font-semibold text-blue-300 mb-3 flex items-center gap-2 text-lg">
            <Users className="w-5 h-5" />
            Current Guest
          </h4>
          <p className="text-blue-200 font-medium text-lg mb-2">
            {currentReservation?.guestName || room.current_guest?.name}
          </p>
          <div className="flex items-center gap-2 text-blue-300 mb-2">
            <Phone className="w-4 h-4" />
            <span className="text-sm">{currentReservation?.guestPhone || room.current_guest?.phone}</span>
          </div>
          <p className="text-sm text-blue-300">
            {new Date(currentReservation?.checkIn || room.check_in!).toLocaleDateString()} - {new Date(currentReservation?.checkOut || room.check_out!).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* Maintenance Notes */}
      {room.maintenance_notes && (
        <div className="mb-6 p-4 bg-red-500/20 rounded-xl border border-red-400/30">
          <h4 className="font-semibold text-red-300 mb-3 flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5" />
            Maintenance Required
          </h4>
          <p className="text-red-200">{room.maintenance_notes}</p>
        </div>
      )}

      {/* Last Cleaned Info */}
      <div className="mb-6 p-4 bg-gold-500/20 rounded-xl border border-gold-400/30">
        <p className="text-sm text-gold-300 mb-1">Last cleaned:</p>
        <p className="text-gold-200 font-medium">{new Date(room.last_cleaned).toLocaleDateString()}</p>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        {room.status === 'available' && (
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => handleCheckIn(room)}
              className="bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 transition-colors font-medium text-lg"
            >
              Check In Guest
            </button>
            <button
              onClick={() => updateRoomStatus(room.room_number, 'cleaning')}
              className="bg-yellow-600 text-white py-3 px-4 rounded-xl hover:bg-yellow-700 transition-colors font-medium"
            >
              Start Cleaning
            </button>
          </div>
        )}
        
        {room.status === 'occupied' && (
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => handleCheckOut(room)}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-colors font-medium text-lg"
            >
              Check Out Guest
            </button>
            <button
              onClick={() => handleCheckIn(room)}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Book Future Dates
            </button>
          </div>
        )}
        
        {room.status === 'cleaning' && (
          <button
            onClick={() => updateRoomStatus(room.room_number, 'available')}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-colors font-medium text-lg"
          >
            Mark Available
          </button>
        )}
        
        {room.status === 'maintenance' && (
          <button
            onClick={() => updateRoomStatus(room.room_number, 'available')}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-colors font-medium text-lg"
          >
            Mark Fixed
          </button>
        )}
        
        <button
          onClick={() => updateRoomStatus(room.room_number, 'maintenance')}
          className="w-full bg-red-600 text-white py-3 px-4 rounded-xl hover:bg-red-700 transition-colors font-medium"
        >
          Report Issue
        </button>
      </div>
    </div>
  );
}

// Real-time Overview Dashboard Component
function OverviewDashboard({ rooms, statistics, refreshData }: any) {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Calculate today's revenue
  const todayRevenue = rooms
    .filter((room: Room) => room.status === 'occupied')
    .reduce((total: number, room: Room) => total + room.price, 0);

  // Get today's check-ins and check-outs
  const todayCheckIns = rooms.filter((room: Room) => 
    room.check_in && new Date(room.check_in).toDateString() === new Date().toDateString()
  );
  
  const todayCheckOuts = rooms.filter((room: Room) => 
    room.check_out && new Date(room.check_out).toDateString() === new Date().toDateString()
  );

  // Get rooms needing attention
  const roomsNeedingAttention = rooms.filter((room: Room) => 
    room.status === 'maintenance' || room.status === 'cleaning'
  );

  return (
    <div className="space-y-8">
      {/* Header with Real-time Info */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gold-400 mb-2">Hotel Manager Dashboard</h2>
            <p className="text-cream-200 text-lg">Real-time overview of Hotel A.S.K Grand operations</p>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-sm text-cream-400">Current Time</p>
              <p className="text-xl font-bold text-gold-400">
                {currentTime.toLocaleTimeString('en-IN', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  timeZone: 'Asia/Kolkata'
                })}
              </p>
            </div>
            <button
              onClick={refreshData}
              className="flex items-center gap-2 px-6 py-3 bg-gold-gradient text-navy-900 rounded-xl hover:shadow-gold transition-all duration-300 font-medium"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Today's Revenue */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-green-400/20 bg-green-500/10">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-400/30">
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-green-300 font-medium">Today's Revenue</p>
              <p className="text-3xl font-bold text-green-400">₹{todayRevenue.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-green-300">From {statistics?.occupied || 0} occupied rooms</p>
        </div>

        {/* Occupancy Rate */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-blue-400/20 bg-blue-500/10">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-400/30">
              <Users className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-blue-300 font-medium">Occupancy Rate</p>
              <p className="text-3xl font-bold text-blue-400">{statistics?.occupancy_rate || 0}%</p>
            </div>
          </div>
          <p className="text-xs text-blue-300">{statistics?.occupied || 0} of {statistics?.total_rooms || 18} rooms occupied</p>
        </div>

        {/* Available Rooms */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20 bg-gold-500/10">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-gold-500/20 rounded-xl border border-gold-400/30">
              <CheckCircle className="w-8 h-8 text-gold-400" />
            </div>
            <div>
              <p className="text-sm text-gold-300 font-medium">Available Rooms</p>
              <p className="text-3xl font-bold text-gold-400">{statistics?.available || 0}</p>
            </div>
          </div>
          <p className="text-xs text-gold-300">Ready for new bookings</p>
        </div>

        {/* Rooms Needing Attention */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-red-400/20 bg-red-500/10">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-red-500/20 rounded-xl border border-red-400/30">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-red-300 font-medium">Need Attention</p>
              <p className="text-3xl font-bold text-red-400">{roomsNeedingAttention.length}</p>
            </div>
          </div>
          <p className="text-xs text-red-300">Maintenance & cleaning required</p>
        </div>
      </div>

      {/* Today's Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Check-ins Today */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gold-400">Today's Check-ins</h3>
            <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
              {todayCheckIns.length} guests
            </span>
          </div>
          
          <div className="space-y-4 max-h-64 overflow-y-auto">
            {todayCheckIns.length > 0 ? (
              todayCheckIns.map((room: Room) => (
                <div key={room.room_number} className="flex items-center justify-between p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
                  <div>
                    <p className="font-bold text-blue-300">Suite {room.room_number}</p>
                    <p className="text-blue-200">{room.current_guest?.name}</p>
                    <p className="text-xs text-blue-400">{room.current_guest?.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-400 font-bold">₹{room.price}</p>
                    <p className="text-xs text-blue-300">per night</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                <p className="text-cream-400">No check-ins scheduled for today</p>
              </div>
            )}
          </div>
        </div>

        {/* Check-outs Today */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gold-400">Today's Check-outs</h3>
            <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
              {todayCheckOuts.length} departures
            </span>
          </div>
          
          <div className="space-y-4 max-h-64 overflow-y-auto">
            {todayCheckOuts.length > 0 ? (
              todayCheckOuts.map((room: Room) => (
                <div key={room.room_number} className="flex items-center justify-between p-4 bg-green-500/10 rounded-xl border border-green-400/20">
                  <div>
                    <p className="font-bold text-green-300">Suite {room.room_number}</p>
                    <p className="text-green-200">{room.current_guest?.name}</p>
                    <p className="text-xs text-green-400">{room.current_guest?.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold">₹{room.price}</p>
                    <p className="text-xs text-green-300">per night</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-cream-400 mx-auto mb-3" />
                <p className="text-cream-400">No check-outs scheduled for today</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Room Status Overview */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gold-400">Room Status Overview</h3>
          <div className="flex items-center gap-2 text-sm text-cream-300">
            <Clock className="w-4 h-4 text-gold-400" />
            <span>Last updated: {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="text-center p-4 bg-green-500/10 rounded-xl border border-green-400/20">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-400">{statistics?.available || 0}</p>
            <p className="text-sm text-green-300">Available</p>
          </div>
          
          <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
            <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-400">{statistics?.occupied || 0}</p>
            <p className="text-sm text-blue-300">Occupied</p>
          </div>
          
          <div className="text-center p-4 bg-yellow-500/10 rounded-xl border border-yellow-400/20">
            <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-400">{statistics?.cleaning || 0}</p>
            <p className="text-sm text-yellow-300">Cleaning</p>
          </div>
          
          <div className="text-center p-4 bg-red-500/10 rounded-xl border border-red-400/20">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-400">{statistics?.maintenance || 0}</p>
            <p className="text-sm text-red-300">Maintenance</p>
          </div>
          
          <div className="text-center p-4 bg-gold-500/10 rounded-xl border border-gold-400/20">
            <Star className="w-8 h-8 text-gold-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gold-400">{statistics?.total_rooms || 18}</p>
            <p className="text-sm text-gold-300">Total Suites</p>
          </div>
        </div>

        {/* Room Type Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {['Single', 'Royal', 'Suite'].map((type) => {
            const typeRooms = rooms.filter((room: Room) => room.type === type);
            const occupied = typeRooms.filter((room: Room) => room.status === 'occupied').length;
            const available = typeRooms.filter((room: Room) => room.status === 'available').length;
            const revenue = typeRooms
              .filter((room: Room) => room.status === 'occupied')
              .reduce((total: number, room: Room) => total + room.price, 0);

            return (
              <div key={type} className="p-4 bg-white/5 rounded-xl border border-gold-400/10">
                <h4 className="font-bold text-gold-400 mb-3">{type} Rooms</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-cream-300">Total:</span>
                    <span className="font-bold text-white">{typeRooms.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cream-300">Occupied:</span>
                    <span className="font-bold text-blue-400">{occupied}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cream-300">Available:</span>
                    <span className="font-bold text-green-400">{available}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gold-400/20">
                    <span className="text-cream-300">Today's Revenue:</span>
                    <span className="font-bold text-gold-400">₹{revenue.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <h3 className="text-xl font-bold text-gold-400 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => {
              // Switch to management tab
              const managementTab = document.querySelector('[data-tab="management"]') as HTMLButtonElement;
              managementTab?.click();
            }}
            className="flex flex-col items-center gap-3 p-6 bg-blue-500/10 rounded-xl border border-blue-400/20 hover:bg-blue-500/20 transition-colors"
          >
            <Users className="w-8 h-8 text-blue-400" />
            <span className="text-blue-300 font-medium">Manage Rooms</span>
          </button>
          
          <button
            onClick={refreshData}
            className="flex flex-col items-center gap-3 p-6 bg-green-500/10 rounded-xl border border-green-400/20 hover:bg-green-500/20 transition-colors"
          >
            <RefreshCw className="w-8 h-8 text-green-400" />
            <span className="text-green-300 font-medium">Refresh Data</span>
          </button>
          
          <button className="flex flex-col items-center gap-3 p-6 bg-gold-500/10 rounded-xl border border-gold-400/20 hover:bg-gold-500/20 transition-colors">
            <BarChart3 className="w-8 h-8 text-gold-400" />
            <span className="text-gold-300 font-medium">View Analytics</span>
          </button>
          
          <button className="flex flex-col items-center gap-3 p-6 bg-gold-500/10 rounded-xl border border-gold-400/20 hover:bg-gold-500/20 transition-colors">
            <Calendar className="w-8 h-8 text-gold-400" />
            <span className="text-gold-300 font-medium">Bookings</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Room Management Tab Component (combines overview and management)
function SuiteManagementTab({ 
  rooms, 
  statistics, 
  searchTerm, 
  setSearchTerm, 
  filterStatus, 
  setFilterStatus, 
  filterType, 
  setFilterType, 
  updateRoomStatus, 
  checkInGuest, 
  checkOutGuest, 
  refreshData, 
  getStatusColor, 
  getStatusIcon 
}: any) {
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  const handleCheckIn = (room: Room) => {
    setSelectedRoom(room);
    setShowCheckInModal(true);
  };

  const handleCheckOut = (room: Room) => {
    setSelectedRoom(room);
    setShowCheckOutModal(true);
  };

  return (
    <div className="space-y-8">
      {/* Header with Statistics */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gold-400 mb-2">Room Management</h2>
            <p className="text-cream-200">Complete room management and guest services</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={refreshData}
              className="flex items-center gap-2 px-4 py-2 bg-gold-gradient text-navy-900 rounded-xl hover:shadow-gold transition-all duration-300 font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        {statistics && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="glass-luxury p-4 rounded-xl border border-green-400/20 bg-green-500/10">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium text-green-300">Available</span>
              </div>
              <p className="text-2xl font-bold text-green-400">{statistics.available}</p>
            </div>
            
            <div className="glass-luxury p-4 rounded-xl border border-blue-400/20 bg-blue-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-blue-300">Occupied</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">{statistics.occupied}</p>
            </div>
            
            <div className="glass-luxury p-4 rounded-xl border border-yellow-400/20 bg-yellow-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-medium text-yellow-300">Cleaning</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{statistics.cleaning}</p>
            </div>
            
            <div className="glass-luxury p-4 rounded-xl border border-red-400/20 bg-red-500/10">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-sm font-medium text-red-300">Maintenance</span>
              </div>
              <p className="text-2xl font-bold text-red-400">{statistics.maintenance}</p>
            </div>
            
            <div className="glass-luxury p-4 rounded-xl border border-gold-400/20 bg-gold-500/10">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-gold-400" />
                <span className="text-sm font-medium text-gold-300">Occupancy</span>
              </div>
              <p className="text-2xl font-bold text-gold-400">{statistics.occupancy_rate}%</p>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gold-400" />
            <input
              type="text"
              placeholder="Search luxury suites or guests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white"
          >
            <option value="all" className="bg-navy-900">All Status</option>
            <option value="available" className="bg-navy-900">Available</option>
            <option value="occupied" className="bg-navy-900">Occupied</option>
            <option value="maintenance" className="bg-navy-900">Maintenance</option>
            <option value="cleaning" className="bg-navy-900">Cleaning</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white"
          >
            <option value="all" className="bg-navy-900">All Types</option>
            <option value="Single" className="bg-navy-900">Single Room</option>
            <option value="Royal" className="bg-navy-900">Royal Room</option>
            <option value="Suite" className="bg-navy-900">Suite Room</option>
          </select>

          <div className="flex items-center gap-2 text-sm text-cream-300">
            <Filter className="w-4 h-4 text-gold-400" />
            <span>{rooms.length} suites</span>
          </div>
        </div>
      </div>

      {/* Room Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
        {rooms.map((room: Room) => (
          <RoomCard
            key={room.room_number}
            room={room}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            updateRoomStatus={updateRoomStatus}
            handleCheckIn={handleCheckIn}
            handleCheckOut={handleCheckOut}
          />
        ))}
      </div>

      {/* Check-in Modal */}
      {showCheckInModal && selectedRoom && (
        <EnhancedCheckInModal
          room={selectedRoom}
          onClose={() => {
            setShowCheckInModal(false);
            setSelectedRoom(null);
          }}
          onCheckIn={checkInGuest}
        />
      )}

      {/* Check-out Modal */}
      {showCheckOutModal && selectedRoom && (
        <LuxuryCheckOutModal
          room={selectedRoom}
          onClose={() => {
            setShowCheckOutModal(false);
            setSelectedRoom(null);
          }}
          onCheckOut={checkOutGuest}
        />
      )}
    </div>
  );
}

// Analytics Tab Component
function AnalyticsTab({ analytics, statistics }: any) {
  if (!analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-8 border border-gold-400/20 text-center">
          <BarChart3 className="w-12 h-12 text-gold-400 mx-auto mb-4" />
          <p className="text-cream-200">Loading luxury analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gold-400 mb-2">Analytics Dashboard</h2>
            <p className="text-cream-200">Performance insights and revenue analytics</p>
          </div>
          <p className="text-sm text-cream-400">
            Last updated: {new Date(analytics.generated_at).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-400/30">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-green-300 font-medium">Today's Revenue</p>
              <p className="text-2xl font-bold text-green-400">₹{analytics.revenue.today.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-400/30">
              <Calendar className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-blue-300 font-medium">Monthly Revenue</p>
              <p className="text-2xl font-bold text-blue-400">₹{analytics.revenue.month.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gold-500/20 rounded-xl border border-gold-400/30">
              <TrendingUp className="w-6 h-6 text-gold-400" />
            </div>
            <div>
              <p className="text-sm text-purple-300 font-medium">Occupancy Rate</p>
              <p className="text-2xl font-bold text-purple-400">{statistics?.occupancy_rate}%</p>
            </div>
          </div>
        </div>

        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gold-500/20 rounded-xl border border-gold-400/30">
              <Star className="w-6 h-6 text-gold-400" />
            </div>
            <div>
              <p className="text-sm text-gold-300 font-medium">Average Rating</p>
              <p className="text-2xl font-bold text-gold-400">{analytics.bookings.average_rating}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Forecast Chart */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <h3 className="text-xl font-bold text-gold-400 mb-6">7-Day Occupancy Forecast</h3>
        <div className="space-y-4">
          {analytics.forecast.map((day: any, index: number) => (
            <div key={index} className="glass-luxury p-4 rounded-xl border border-gold-400/10 hover:border-gold-400/30 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-gold-400">{day.day_name}</p>
                  <p className="text-sm text-cream-300">{day.date}</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-white">{day.predicted_occupancy}/18 suites</p>
                  <p className="text-sm text-cream-400">{day.occupancy_rate}% occupancy</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-400">₹{day.estimated_revenue.toLocaleString()}</p>
                  <p className="text-sm text-green-300">Est. revenue</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Room Type Performance */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <h3 className="text-xl font-bold text-gold-400 mb-6">Suite Type Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(analytics.room_types).map(([type, data]: [string, any]) => (
            <div key={type} className="glass-luxury p-6 rounded-xl border border-gold-400/10">
              <h4 className="font-bold text-gold-400 mb-4 text-lg">{type} Suites</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-cream-300">Total Suites:</span>
                  <span className="font-bold text-white">{data.count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-cream-300">Currently Occupied:</span>
                  <span className="font-bold text-blue-400">{data.occupied}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-cream-300">Monthly Revenue:</span>
                  <span className="font-bold text-green-400">₹{data.revenue.toLocaleString()}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-gold-400/20">
                  <div className="flex justify-between items-center">
                    <span className="text-cream-300">Occupancy Rate:</span>
                    <span className="font-bold text-gold-400">{Math.round((data.occupied / data.count) * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <h3 className="text-xl font-bold text-gold-400 mb-4">Booking Statistics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Total Bookings:</span>
              <span className="font-bold text-white">{analytics.bookings.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Completion Rate:</span>
              <span className="font-bold text-green-400">{analytics.bookings.completion_rate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Average Rating:</span>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gold-400">{analytics.bookings.average_rating}</span>
                <Star className="w-4 h-4 text-gold-400 fill-current" />
              </div>
            </div>
          </div>
        </div>

        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
          <h3 className="text-xl font-bold text-gold-400 mb-4">Maintenance Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Rooms Needing Attention:</span>
              <span className="font-bold text-red-400">{analytics.maintenance.rooms_needing_attention}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Last Deep Clean:</span>
              <span className="font-bold text-white">{new Date(analytics.maintenance.last_deep_clean).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-cream-300">Next Scheduled:</span>
              <span className="font-bold text-yellow-400">{new Date(analytics.maintenance.next_scheduled).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Luxury Data Management Component
function LuxuryDataManagement() {
  const [activeTab, setActiveTab] = useState('backup');
  const [loading, setLoading] = useState(false);

  const tabs = [
    { id: 'backup', label: 'Backup & Restore', icon: Database },
    { id: 'export', label: 'Export & Import', icon: DollarSign },
    { id: 'cloud', label: 'Cloud Storage', icon: Star },
  ];

  const handleCreateBackup = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      alert('Backup created successfully!');
    }, 2000);
  };

  const handleExportData = (format: string) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      alert(`Data exported as ${format.toUpperCase()} successfully!`);
    }, 1500);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <h2 className="text-2xl font-bold text-gold-400 mb-2">Data Management</h2>
        <p className="text-cream-200">Backup, export, and manage your luxury hotel data</p>
      </div>

      {/* Tabs */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex flex-col lg:flex-row gap-2 mb-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  activeTab === tab.id 
                    ? 'bg-gold-gradient text-navy-900 shadow-gold' 
                    : 'text-cream-200 hover:bg-gold-400/10 hover:text-gold-400'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'backup' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-500/10 rounded-xl border border-green-400/20">
                <h3 className="text-lg font-bold text-green-400 mb-4">Create Backup</h3>
                <p className="text-cream-300 mb-4">Create a complete backup of all hotel data</p>
                <button
                  onClick={handleCreateBackup}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
                >
                  {loading ? 'Creating...' : 'Create Backup'}
                </button>
              </div>
              
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-400/20">
                <h3 className="text-lg font-bold text-blue-400 mb-4">Restore Data</h3>
                <p className="text-cream-300 mb-4">Restore from previous backup</p>
                <button className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium">
                  Select Backup
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'export' && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gold-400 mb-4">Export Hotel Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['JSON', 'CSV', 'Excel'].map((format) => (
                <button
                  key={format}
                  onClick={() => handleExportData(format)}
                  disabled={loading}
                  className="p-6 bg-white/5 border border-gold-400/20 rounded-xl hover:bg-white/10 hover:scale-105 transition-all duration-300 disabled:opacity-50"
                >
                  <Database className="w-8 h-8 text-gold-400 mx-auto mb-3" />
                  <h4 className="font-bold text-white mb-2">{format} Format</h4>
                  <p className="text-cream-300 text-sm">Export as {format}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'cloud' && (
          <div className="space-y-6">
            <div className="text-center p-8 bg-gold-500/10 rounded-xl border border-gold-400/20">
              <Star className="w-12 h-12 text-gold-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gold-400 mb-2">Cloud Storage</h3>
              <p className="text-cream-300 mb-4">Sync your data to the cloud for backup and access</p>
              <button className="bg-gold-gradient text-navy-900 px-8 py-3 rounded-xl hover:shadow-gold transition-all duration-300 font-medium">
                Configure Cloud Storage
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Luxury Settings Component
function LuxurySettings() {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    hotelName: 'Hotel A.S.K Grand',
    phone: '+91 7799933325',
    email: 'askgrandgajuwaka@gmail.com',
    address: '10-2-9/1, Mehar Nagar, Old Gajuwaka, Visakhapatnam, Andhra Pradesh 530026',
    checkInTime: '14:00',
    checkOutTime: '12:00',
    currency: 'INR',
    timezone: 'Asia/Kolkata'
  });

  const tabs = [
    { id: 'general', label: 'General Settings', icon: SettingsIcon },
    { id: 'notifications', label: 'Notifications', icon: AlertTriangle },
    { id: 'security', label: 'Security', icon: CheckCircle },
  ];

  const handleSave = () => {
    alert('Settings saved successfully!');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <h2 className="text-2xl font-bold text-gold-400 mb-2">Hotel Settings</h2>
        <p className="text-cream-200">Configure your luxury hotel preferences and settings</p>
      </div>

      {/* Tabs */}
      <div className="glass-luxury rounded-3xl shadow-luxury-xl p-6 border border-gold-400/20">
        <div className="flex flex-col lg:flex-row gap-2 mb-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  activeTab === tab.id 
                    ? 'bg-gold-gradient text-navy-900 shadow-gold' 
                    : 'text-cream-200 hover:bg-gold-400/10 hover:text-gold-400'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Hotel Name</label>
                <input
                  type="text"
                  value={settings.hotelName}
                  onChange={(e) => setSettings({...settings, hotelName: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Email Address</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Currency</label>
                <select
                  value={settings.currency}
                  onChange={(e) => setSettings({...settings, currency: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white"
                >
                  <option value="INR" className="bg-navy-900">Indian Rupee (₹)</option>
                  <option value="USD" className="bg-navy-900">US Dollar ($)</option>
                  <option value="EUR" className="bg-navy-900">Euro (€)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gold-300 mb-2">Hotel Address</label>
              <textarea
                value={settings.address}
                onChange={(e) => setSettings({...settings, address: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Check-in Time</label>
                <input
                  type="time"
                  value={settings.checkInTime}
                  onChange={(e) => setSettings({...settings, checkInTime: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Check-out Time</label>
                <input
                  type="time"
                  value={settings.checkOutTime}
                  onChange={(e) => setSettings({...settings, checkOutTime: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gold-400 mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              {[
                { label: 'New Bookings', description: 'Get notified when new bookings are made' },
                { label: 'Check-ins/Check-outs', description: 'Notifications for guest arrivals and departures' },
                { label: 'Maintenance Alerts', description: 'Room maintenance and cleaning notifications' },
                { label: 'Revenue Reports', description: 'Daily and weekly revenue summaries' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-gold-400/20">
                  <div>
                    <h4 className="font-medium text-white">{item.label}</h4>
                    <p className="text-sm text-cream-300">{item.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gold-400 mb-4">Security Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-500/10 rounded-xl border border-green-400/20">
                <CheckCircle className="w-8 h-8 text-green-400 mb-3" />
                <h4 className="font-bold text-green-400 mb-2">Two-Factor Authentication</h4>
                <p className="text-cream-300 text-sm mb-4">Add an extra layer of security</p>
                <button className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors">
                  Enable 2FA
                </button>
              </div>
              
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-400/20">
                <AlertTriangle className="w-8 h-8 text-blue-400 mb-3" />
                <h4 className="font-bold text-blue-400 mb-2">Password Policy</h4>
                <p className="text-cream-300 text-sm mb-4">Configure password requirements</p>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors">
                  Configure
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-6 border-t border-gold-400/20">
          <button
            onClick={handleSave}
            className="bg-gold-gradient text-navy-900 px-8 py-3 rounded-xl hover:shadow-gold transition-all duration-300 font-medium"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

// Luxury Check-out Modal Component
function LuxuryCheckOutModal({ room, onClose, onCheckOut }: any) {
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCheckOut(room.room_number, rating);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-luxury rounded-3xl shadow-luxury-xl max-w-md w-full border border-gold-400/20">
        <div className="p-6 border-b border-gold-400/20">
          <h3 className="text-xl font-bold text-gold-400 mb-2">
            Luxury Suite Check-Out
          </h3>
          <p className="text-cream-300">Suite {room.room_number}</p>
          <p className="text-sm text-blue-300 mt-1">
            Guest: {room.current_guest?.name}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gold-300 mb-3">Guest Experience Rating</label>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`p-2 rounded-lg transition-colors ${
                    star <= rating 
                      ? 'text-gold-400 bg-gold-400/20' 
                      : 'text-cream-400 hover:text-gold-300 hover:bg-white/5'
                  }`}
                >
                  <Star className="w-8 h-8 fill-current" />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-cream-400 mt-2">
              {rating === 5 ? 'Excellent' : rating === 4 ? 'Very Good' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gold-300 mb-2">Guest Feedback (Optional)</label>
            <textarea
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300"
              placeholder="Share your experience at Hotel A.S.K Grand..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gold-400/30 text-cream-300 rounded-xl hover:bg-white/5 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
            >
              Complete Check-Out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}